<template>
  <div class="dl-tab">
    <section v-for="(sec, si) in sections" :key="si" class="dl-sec">
      <div class="dl-sec-head">
        <span class="dl-sec-title">{{ sec.title }}</span>
        <span v-if="sec.tag" class="dl-ver" :class="sec.kind">{{ sec.tag }}</span>
      </div>
      <p v-if="sec.note" class="dl-note">{{ sec.note }}</p>
      <div v-for="(g, gi) in sec.groups" :key="gi" class="dl-group">
        <div v-if="g.name" class="dl-gname">{{ g.name }}</div>
        <div v-for="(it, ii) in g.items" :key="ii" class="dl-row">
          <span class="dl-ic" v-html="icons[it.icon] || icons.file"></span>
          <span class="dl-lab">{{ it.label }}</span>
          <span v-if="it.size" class="dl-size">{{ it.size }}</span>
          <span class="dl-acts">
            <a class="dl-act" :href="it.url" download :title="`Download ${it.label}`">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>
            </a>
            <a
              v-if="signed && it.url"
              class="dl-act dl-act-v"
              :href="verifyUrl()"
              target="_blank"
              rel="noopener noreferrer"
              :title="`Verify Content Credentials: ${it.label}`"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 2.6v5.1c0 4.3-3 8.2-7 9.3-4-1.1-7-5-7-9.3V5.6L12 3z" /><path d="M8.8 11.7l2.2 2.2 4.2-4.6" /></svg>
            </a>
          </span>
        </div>
      </div>
    </section>
    <p v-if="!sections.length" class="ip-empty">No downloads are available for this recording.</p>

    <div v-if="storage" class="dl-storage">
      <StorageUpsell :storage="storage" :event-id="eventId" />
    </div>
  </div>
</template>

<script>
import { versionInfo, normalizeProvenance } from '../useEvent';
import StorageUpsell from './StorageUpsell.vue';

const ICONS = {
  video: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="14" height="14" rx="2"/><path d="M17 9l4-2v10l-4-2"/></svg>',
  audio: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 18V5l11-2v13" stroke-linecap="round"/><circle cx="6" cy="18" r="2.6"/><circle cx="20" cy="16" r="2.6"/></svg>',
  file: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z"/><path d="M14 3v5h5"/></svg>',
};

function iconFor(dwn) {
  const g = (dwn.group || '').toLowerCase();
  const u = (dwn.url || '').toLowerCase();
  if (/(camera|broadcast|bbb|recording|webcam|screen)/.test(g)) return 'video';
  if (/(caption|\.vtt|raw|transcript)/.test(g) || /\.(vtt|srt|txt)$/.test(u)) return 'file';
  if (/\.(m4a|mp3|aac|wav)$/.test(u)) return 'audio';
  if (/\.(mp4|webm|mkv|mov)$/.test(u)) return 'video';
  return 'file';
}

export default {
  name: 'DownloadsTab',
  components: { StorageUpsell },
  props: {
    rec: { type: Object, required: true },
    storage: { type: Object, default: null },
    eventId: { type: String, default: '' },
    /** Narrow the list to one kind of file, chosen from the navigation. */
    focus: { type: String, default: null },
  },
  data() {
    return { icons: ICONS };
  },
  computed: {
    vinfo() {
      return versionInfo(this.rec);
    },
    signed() {
      return !!normalizeProvenance(this.rec);
    },
    /** The list as the navigation asked for it: everything, or one kind of file. */
    sections() {
      if (!this.focus) return this.allSections;
      return this.allSections
        .map((sec) => ({
          ...sec,
          groups: sec.groups
            .map((g) => ({ ...g, items: g.items.filter((i) => i.icon === this.focus) }))
            .filter((g) => g.items.length),
        }))
        .filter((sec) => sec.groups.length);
    },
    allSections() {
      const out = [];
      const m = this.rec.media || {};
      // current / renderable version = the recording's top-level assets
      const cur = [];
      if (m.video) cur.push({ icon: 'video', label: 'Main video', size: m.video.size, url: m.video.url });
      if (m.audio) cur.push({ icon: 'audio', label: 'Audio', size: m.audio.size, url: m.audio.url });
      if (m.transcriptVtt) cur.push({ icon: 'file', label: 'Transcript (.vtt)', url: m.transcriptVtt });
      const primary = this.vinfo.primary;
      if (cur.length) {
        out.push({
          title: (primary && primary.label) || 'AI-optimized',
          tag: (primary && primary.id) || this.rec.currentVersion || null,
          kind: 'optimized',
          note: null,
          groups: [{ name: null, items: cur }],
        });
      }
      if (this.vinfo.hasVersions) {
        this.vinfo.originals.forEach((v) => {
          const byGroup = {};
          const order = [];
          (v.downloads || []).forEach((dwn) => {
            const g = dwn.group || 'Files';
            if (!byGroup[g]) {
              byGroup[g] = [];
              order.push(g);
            }
            byGroup[g].push({ icon: iconFor(dwn), label: dwn.label, size: dwn.size, url: dwn.url });
          });
          out.push({
            title: v.label || 'Originals',
            tag: v.id,
            kind: 'original',
            note: v.note,
            groups: order.map((g) => ({ name: g, items: byGroup[g] })),
          });
        });
      } else {
        // fallback: legacy event.json without versions → attachments from elements.files
        const files = (this.rec.elements && this.rec.elements.files) || [];
        if (files.length) {
          out.push({
            title: 'Attachments',
            tag: null,
            kind: 'original',
            note: null,
            groups: [{ name: null, items: files.map((f) => ({ icon: 'file', label: f.label, size: f.size, url: f.url })) }],
          });
        }
      }
      return out;
    },
  },
  methods: {
    verifyUrl() {
      // the CAI inspector only reads a dropped file (no remote-URL loading) -
      // open it plain; the adjacent download button provides the file
      return 'https://verify.contentauthenticity.org/';
    },
  },
};
</script>
